import { BaseCard } from "../../Components/Cards/BaseCard";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useBoundStore } from "../../Store/store";
import { useEffect, useState } from "react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { SelectInput } from "../../Components/Form/SelectInput";
import { DateTimePickerInput } from "../../Components/Form/DateTimePickerInput";
import { SecondaryButton } from "../../Components/Buttons/SecondaryButton";
import { createColumnHelper } from "@tanstack/react-table";
import { TanStackTable } from "../../Components/Tables/TanStackTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheckCircle, faFileExport } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { truncateString } from "../../Utils/StringHelper";

export const Invoices = () => {
    const { t } = useTranslation();
    const customers = useBoundStore((state) => state.customers) || [];
    const getAllCustomers = useBoundStore((state) => state.getAllCustomers);
    const getAllInvoices = useBoundStore((state) => state.getAllInvoices);
    const markInvoiceAsPaid = useBoundStore((state) => state.markInvoiceAsPaid);
    const markInvoiceAsReadyForExact = useBoundStore((state) => state.markInvoiceAsReadyForExact);

    const [invoices, setInvoices] = useState([]);
    const [metaData, setMetaData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [customerBranches, setCustomerBranches] = useState([]);
    const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);
    const [loadingExactInvoiceId, setLoadingExactInvoiceId] = useState(null);

    const getStoredValue = (key) => {
        if (typeof window === 'undefined') return null;
        const value = localStorage.getItem(key);
        if (value && !isNaN(value)) {
            return Number(value);
        }
        return value || null;
    };

    const getStoredDate = (key) => {
        if (typeof window === 'undefined') return null;
        const stored = localStorage.getItem(key);
        return stored ? new Date(stored) : null;
    };

    const { control, setValue, watch, reset } = useForm({
        defaultValues: {
            customer_id: getStoredValue('admin_invoices_customer_id'),
            branch_id: getStoredValue('admin_invoices_branch_id'),
            from: getStoredDate('admin_invoices_from'),
            to: getStoredDate('admin_invoices_to'),
            status: getStoredValue('admin_invoices_status'),
        }
    });

    const customerId = watch('customer_id');
    const branchId = watch('branch_id');
    const from = watch('from');
    const to = watch('to');
    const status = watch('status');

    const statusOptions = [
        { id: 'pending', value: 'pending', label: t('general.status.open') },
        { id: 'paid', value: 'paid', label: t('general.status.paid') },
        { id: 'ready_for_exact', value: 'ready_for_exact', label: t('admin.invoices.status_ready_for_exact') },
    ];

    useEffect(() => {
        getAllCustomers(null, null, { nopaginate: true });
    }, [getAllCustomers]);

    useEffect(() => {
        if (customerId !== undefined) {
            if (customerId === null || customerId === '') {
                localStorage.removeItem('admin_invoices_customer_id');
            } else {
                localStorage.setItem('admin_invoices_customer_id', customerId);
            }
        }
    }, [customerId]);

    useEffect(() => {
        if (branchId !== undefined) {
            if (branchId === null || branchId === '') {
                localStorage.removeItem('admin_invoices_branch_id');
            } else {
                localStorage.setItem('admin_invoices_branch_id', branchId);
            }
        }
    }, [branchId]);

    useEffect(() => {
        if (from !== undefined) {
            if (from === null) {
                localStorage.removeItem('admin_invoices_from');
            } else {
                localStorage.setItem('admin_invoices_from', from.toISOString());
            }
        }
    }, [from]);

    useEffect(() => {
        if (to !== undefined) {
            if (to === null) {
                localStorage.removeItem('admin_invoices_to');
            } else {
                localStorage.setItem('admin_invoices_to', to.toISOString());
            }
        }
    }, [to]);

    useEffect(() => {
        if (status !== undefined) {
            if (status === null || status === '') {
                localStorage.removeItem('admin_invoices_status');
            } else {
                localStorage.setItem('admin_invoices_status', status);
            }
        }
    }, [status]);

    useEffect(() => {
        if (customerId && customers.length > 0) {
            const customer = customers.find(c => c.id === customerId);
            if (customer && customer.branches) {
                setCustomerBranches(
                    customer.branches.map((branch) => ({
                        id: branch.id,
                        value: branch.id,
                        label: branch.company_name
                    }))
                );
            } else {
                setCustomerBranches([]);
            }
        } else {
            setCustomerBranches([]);
            setValue('branch_id', null);
        }
    }, [customerId, customers, setValue]);

    useEffect(() => {
        setCurrentPage(1);
    }, [customerId, branchId, from, to, status]);

    useEffect(() => {
        fetchInvoices();
    }, [customerId, branchId, from, to, status, currentPage]);

    const fetchInvoices = async () => {
        setIsLoading(true);
        try {
            const filters = {
                page: currentPage,
            };

            if (customerId) filters.customer_id = customerId;
            if (branchId) filters.branch_id = branchId;
            if (from) filters.from = dayjs(from).format('YYYY-MM-DD');
            if (to) filters.to = dayjs(to).format('YYYY-MM-DD');
            if (status) filters.status = status;

            const result = await getAllInvoices(filters);
            setInvoices(result.data);
            setMetaData(result.meta);
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearFilters = () => {
        localStorage.removeItem('admin_invoices_customer_id');
        localStorage.removeItem('admin_invoices_branch_id');
        localStorage.removeItem('admin_invoices_from');
        localStorage.removeItem('admin_invoices_to');
        localStorage.removeItem('admin_invoices_status');

        reset({
            customer_id: null,
            branch_id: null,
            from: null,
            to: null,
            status: null,
        });
        setCurrentPage(1);
    };

    const handleMarkAsPaid = async (invoiceId) => {
        setLoadingInvoiceId(invoiceId);
        const success = await markInvoiceAsPaid(invoiceId);
        if (success) {
            await fetchInvoices();
        }
        setLoadingInvoiceId(null);
    };

    const handleMarkAsReadyForExact = async (invoiceId) => {
        setLoadingExactInvoiceId(invoiceId);
        const success = await markInvoiceAsReadyForExact(invoiceId);
        if (success) {
            await fetchInvoices();
        }
        setLoadingExactInvoiceId(null);
    };

    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor('id', {
            header: t('general.fields.invoice_number'),
            cell: props => {
                const invoiceNumberExact = props.row.original.invoice_number_exact;
                return <span className="text-sm font-medium">{truncateString(invoiceNumberExact)}</span>;
            },
        }),
        columnHelper.accessor('contract_type', {
            header: t('general.fields.contract_type'),
            cell: props => {
                const branch = props.row.original.branch;
                const freelancer = props.row.original.freelancer;
                const branchId = branch?.id;
                const freelancerId = freelancer?.id;

                let contractType = t('admin.invoices.contract_type_normal');
                if (branchId === 0) {
                    contractType = t('admin.invoices.contract_type_freelance');
                } else if (freelancerId === 0) {
                    contractType = t('admin.invoices.contract_type_customer');
                }

                return <span className="text-sm">{contractType}</span>;
            },
        }),
        columnHelper.accessor('performance_statement', {
            header: t('general.fields.period'),
            cell: props => {
                const statement = props.cell.getValue();
                return statement?.from?.timestamp ? (
                    <span className="text-sm">{dayjs.unix(statement.from.timestamp).format('DD/MM/YYYY')}</span>
                ) : '-';
            },
        }),
        columnHelper.accessor('freelancer', {
            header: i18next.t('shifts_module.column_freelancer'),
            cell: props => {
                const freelancer = props.cell.getValue();
                if(freelancer?.id === 0){
                    return <span className="text-sm">Projects In Care ({props.row.original?.performance_statement?.freelancer?.first_name ?? '-'} {props.row.original?.performance_statement?.freelancer?.last_name ?? ''})</span>;
                }

                return freelancer ? (
                    <span className="text-sm">{truncateString(`${freelancer.firstname ?? '-'} ${freelancer.lastname ?? ''}`)}</span>
                ) : '-';
            },
        }),
        columnHelper.accessor('branch', {
            header: t('general.fields.branch'),
            cell: props => {
                const branch = props.cell.getValue();
                if(branch?.id === 0){
                    return <span className="text-sm">Projects In Care ({props.row.original?.performance_statement?.branch?.company_name ?? ''})</span>;
                }
                return branch ? (
                    <span className="text-sm">{truncateString(branch.company_name)}</span>
                ) : '-';
            },
        }),
        columnHelper.accessor('amount', {
            header: t('general.fields.amount'),
            cell: props => {
                const amount = props.cell.getValue();
                return amount ? (
                    <span className="text-sm font-medium">€ {parseFloat(amount).toFixed(2)}</span>
                ) : '-';
            },
        }),
        columnHelper.accessor('status', {
            header: t('general.fields.actions'),
            cell: props => {
                const isPaid = props.row.original.status === 'paid';
                const isReadyForExact = props.row.original.ready_for_exact;
                const isLoadingPaid = loadingInvoiceId === props.row.original.id;
                const isLoadingExact = loadingExactInvoiceId === props.row.original.id;
                const freelancer = props.row.original.freelancer;
                const isKlantfactuur = freelancer?.id === 0;

                return (
                    <div className={'flex flex-row space-x-2 items-center'}>
                        <div className={'flex flex-row space-x-1'}>
                            {['pending', 'open'].includes(props.row.original.status) ?
                                <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('general.status.open')}</div> : ''}
                            {isPaid ?
                                <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('general.status.paid')}</div> : ''}
                            {isReadyForExact ?
                                <div className={'px-2 py-1 bg-blue-300 text-blue-900 rounded-full text-xs opacity-70'}>{t('invoices.sent_to_exact')}</div> : ''}
                        </div>
                        {!isPaid && (
                            <button
                                onClick={() => handleMarkAsPaid(props.row.original.id)}
                                disabled={isLoadingPaid}
                                className={`flex items-center justify-center bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 ${isLoadingPaid ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`}
                                title={t('invoices.mark_as_paid')}
                            >
                                {isLoadingPaid ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCheckCircle} className={'mr-1'} />
                                        {t('general.status.paid')}
                                    </>
                                )}
                            </button>
                        )}
                        {!isReadyForExact && (
                            <button
                                onClick={() => handleMarkAsReadyForExact(props.row.original.id)}
                                disabled={isLoadingExact || isKlantfactuur}
                                className={`flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 ${(isLoadingExact || isKlantfactuur) ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`}
                                title={isKlantfactuur ? t('invoices.customer_invoice_no_exact') : t('invoices.mark_ready_exact')}
                            >
                                {isLoadingExact ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faFileExport} className={'mr-1'} />
                                        {t('invoices.exact')}
                                    </>
                                )}
                            </button>
                        )}
                        <Link
                            to={`/admin/invoices/${props.row.original.id}`}
                            className={'flex bg-primary w-7 h-7 rounded-full text-white flex-col justify-center border border-primary hover:bg-transparent hover:border-primary hover:text-primary transition-all duration-300'}
                        >
                            <FontAwesomeIcon icon={faArrowRight} className={'text-sm'} />
                        </Link>
                    </div>
                );
            },
        })
    ];

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('invoices.title')}</h1>

            <BaseCard border={false} className={'mb-4'}>
                <div className={'grid grid-cols-1 gap-4 md:grid-cols-5'}>
                    <div>
                        <SelectInput
                            name={'customer_id'}
                            options={customers.map((customer) => ({
                                id: customer?.id,
                                value: customer?.id,
                                label: `${customer?.firstname} ${customer?.lastname}`
                            }))}
                            clearable={true}
                            control={control}
                            setValue={(fieldName, value) => {
                                setValue('customer_id', value);
                                setValue('branch_id', null);
                            }}
                            label={t('invoices.filter_customer')}
                        />
                    </div>

                    <div>
                        <SelectInput
                            name={'branch_id'}
                            options={customerBranches}
                            control={control}
                            setValue={setValue}
                            clearable={true}
                            label={t('invoices.filter_branch')}
                        />
                    </div>

                    <div>
                        <DateTimePickerInput
                            name={'from'}
                            control={control}
                            setValue={setValue}
                            translationIndex={'invoices.filters.from'}
                            dateFormat="dd/MM/yyyy"
                            showTimeInput={false}
                            isClearable={true}
                        />
                    </div>

                    <div>
                        <DateTimePickerInput
                            name={'to'}
                            control={control}
                            setValue={setValue}
                            translationIndex={'invoices.filters.to'}
                            dateFormat="dd/MM/yyyy"
                            showTimeInput={false}
                            isClearable={true}
                        />
                    </div>

                    <div>
                        <SelectInput
                            name={'status'}
                            options={statusOptions}
                            control={control}
                            setValue={setValue}
                            clearable={true}
                            label={t('invoices.filter_status')}
                        />
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <SecondaryButton onClick={handleClearFilters}>
                        {t('invoices.clear_filters')}
                    </SecondaryButton>
                </div>
            </BaseCard>

            <BaseCard>
                <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('invoices.overview')}</h3>
                    <p className="text-sm text-gray-600">
                        {t('invoices.total_count', {count: metaData?.total || 0})}
                    </p>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    </div>
                ) : invoices.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                        {t('invoices.no_results')}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <TanStackTable
                            columns={columns}
                            tableData={invoices}
                            laravelPaginationMetaData={metaData}
                            page={currentPage - 1}
                            onChangePage={(pageIndex) => setCurrentPage(pageIndex + 1)}
                        />
                    </div>
                )}
            </BaseCard>
        </div>
    );
};
